import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, XCircle, ExternalLink, Plus, Tag, CalendarDays, Camera, ClipboardList, ChefHat, Truck, Pencil } from "lucide-react";
import { api, gbp } from "@/lib/api";

const WORKER_LABELS = {
  "reservation-expiry": "Reservation expiry",
  "email-outbox": "Email outbox",
};

const QUICK_ACTIONS = [
  { to: "/admin/products/new", label: "Add a product", icon: Plus, testid: "quick-add-product" },
  { to: "/admin/availability", label: "Update prices", icon: Tag, testid: "quick-update-prices" },
  { to: "/admin/availability", label: "Update availability", icon: CalendarDays, testid: "quick-update-availability" },
  { to: "/admin/photos", label: "Upload photos", icon: Camera, testid: "quick-upload-photos" },
  { to: "/admin/orders", label: "View orders", icon: ClipboardList, testid: "quick-view-orders" },
  { to: "/admin/production", label: "This week's production", icon: ChefHat, testid: "quick-view-production" },
  { to: "/admin/delivery", label: "Edit delivery slots", icon: Truck, testid: "quick-edit-slots" },
  { to: "/admin/content", label: "Edit homepage", icon: Pencil, testid: "quick-edit-homepage" },
];

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/health").then((r) => setHealth(r.data)).catch(() => setHealth(false));
    api.get("/admin/settings/setup-checklist").then((r) => setChecklist(r.data)).catch(() => {});
  }, []);

  const o = overview?.owner || {};
  const remaining = checklist ? checklist.items.filter((i) => !i.done) : [];
  const storageBroken = health && health.storage.backend === "objstore" && health.storage.ok === false;

  const STATUS_ROWS = health && [
    { label: "Email service", ok: true, note: `TEST MODE (${health.email_gate.mode}) — blocked until the sending domain is verified`, warn: true, testid: "status-email" },
    { label: "Payment service", ok: true, note: "Stripe connected (test mode until go-live)", testid: "status-payments" },
    { label: "Delivery", ok: true, note: "Off — collection only (zones are inactive drafts)", warn: true, testid: "status-delivery" },
    { label: "Image storage", ok: health.storage.backend === "objstore" ? health.storage.ok !== false : true, note: health.storage.backend === "objstore" ? "Durable object storage" : "Local disk (development)", testid: "status-storage" },
    { label: "Reservation worker", ok: !health.workers["reservation-expiry"]?.stale, note: health.workers["reservation-expiry"]?.stale ? "No recent run — orders may not expire" : "Running", testid: "status-worker" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Overview</h1>

      {health && (!health.workers_healthy || storageBroken || health.transactions_supported === false) && (
        <div className="mt-6 rounded-3xl border border-red-500/50 bg-red-500/10 p-5" data-testid="health-alert-panel" role="alert">
          <p className="flex items-center gap-2 text-sm font-bold text-red-300">
            <XCircle className="h-4 w-4" aria-hidden="true" /> Operational problem — act before taking orders
          </p>
          <ul className="mt-2 space-y-1 text-sm text-red-200">
            {Object.entries(health.workers).filter(([, w]) => w.stale).map(([key, w]) => (
              <li key={key} data-testid={`health-worker-stale-${key}`}>
                {WORKER_LABELS[key] || key} worker: no successful run {w.age_seconds != null ? `for ${Math.round(w.age_seconds / 60)} min` : "yet"}
                {w.last_error ? ` (last error: ${w.last_error})` : ""}.
              </li>
            ))}
            {storageBroken && <li data-testid="health-storage-broken">Image storage is unreachable — uploads will fail.</li>}
            {health.transactions_supported === false && <li data-testid="health-tx-broken">Database transactions unavailable — checkout is unsafe.</li>}
          </ul>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-stats-grid">
        {[
          ["New orders", o.new_orders, "/admin/orders", "stat-new-orders"],
          ["Need attention", o.attention_orders, "/admin/orders", "stat-attention"],
          ["Production this week", o.production_units_this_week, "/admin/production", "stat-production"],
          ["To collect on pickup", o.outstanding_collection_pence != null ? gbp(o.outstanding_collection_pence) : "—", "/admin/production", "stat-outstanding"],
          ["Live products", o.live_products, "/admin/products", "stat-live"],
          ["Sold out", o.sold_out_products, "/admin/availability", "stat-soldout"],
          ["Unavailable", o.unavailable_products, "/admin/products", "stat-unavailable"],
          ["Need allergen review", o.products_needing_review, "/admin/products", "stat-need-review"],
        ].map(([label, value, to, testid]) => (
          <Link key={testid} to={to} className="rounded-3xl border border-choco-line bg-choco-light p-4 transition-colors hover:border-gold/50" data-testid={testid}>
            <p className="font-serif text-2xl font-bold text-gold">{value ?? "…"}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-cream-muted">{label}</p>
          </Link>
        ))}
      </div>

      <h2 className="mt-8 font-serif text-lg font-semibold text-cream-text">Quick actions</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="quick-actions">
        {QUICK_ACTIONS.map(({ to, label, icon: Icon, testid }) => (
          <Link key={testid} to={to} className="flex min-h-[64px] items-center gap-2.5 rounded-2xl border border-choco-line bg-choco-light px-4 py-3 text-sm font-bold text-cream-text transition-colors hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus" data-testid={testid}>
            <Icon className="h-5 w-5 shrink-0 text-gold" aria-hidden="true" /> {label}
          </Link>
        ))}
        <a href="/" target="_blank" rel="noreferrer" className="flex min-h-[64px] items-center gap-2.5 rounded-2xl border border-choco-line bg-choco-light px-4 py-3 text-sm font-bold text-cream-text transition-colors hover:border-gold hover:text-gold" data-testid="quick-view-site">
          <ExternalLink className="h-5 w-5 shrink-0 text-gold" aria-hidden="true" /> View live website
        </a>
      </div>

      {checklist && remaining.length > 0 && (
        <div className="mt-8 rounded-3xl border border-amber-500/40 bg-amber-500/10 p-5" data-testid="launch-tasks-panel">
          <p className="text-sm font-bold text-amber-200">Setup remaining — {checklist.items.length - remaining.length} of {checklist.items.length} done</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-100/90">
            {remaining.slice(0, 6).map((i) => (
              <li key={i.key}>· {i.label}</li>
            ))}
            {remaining.length > 6 && <li>· and {remaining.length - 6} more…</li>}
          </ul>
          <Link to="/admin/help" className="mt-3 inline-block rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid="launch-tasks-link">Open setup checklist</Link>
        </div>
      )}

      {overview?.warnings?.length > 0 && (
        <div className="mt-6 space-y-2 rounded-3xl border border-amber-500/50 bg-amber-500/10 p-5" data-testid="admin-warnings-panel">
          {overview.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-2 text-sm text-amber-200" data-testid={`admin-warning-${i}`}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {w}
            </p>
          ))}
        </div>
      )}

      <div className="mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="admin-health-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">System health</h2>
        {health === null && <p className="mt-2 text-sm text-cream-muted">Checking…</p>}
        {health === false && <p className="mt-2 text-sm text-red-400">Health check unavailable.</p>}
        {health && (
          <ul className="mt-3 space-y-2 text-sm text-cream-muted">
            {STATUS_ROWS.map((row) => (
              <li key={row.testid} className="flex flex-wrap items-center gap-2" data-testid={row.testid}>
                {row.ok ? (
                  <CheckCircle2 className={`h-4 w-4 ${row.warn ? "text-amber-300" : "text-green-400"}`} aria-hidden="true" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-400" aria-hidden="true" />
                )}
                <span className="font-semibold text-cream-text">{row.label}</span>
                <span>{row.note}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
